import { circle } from "@turf/circle";

/**
 * The bands, outermost first, paired with the setting that sizes each one.
 *
 * Outermost first so the features come back in draw order: the Data layer
 * paints later features over earlier ones, and a stroke that lands on a shared
 * boundary should be the inner band's.
 */
export const PROXIMITY_BANDS = [
  { band: "red", limitKey: "max" },
  { band: "yellow", limitKey: "mid" },
  { band: "green", limitKey: "min" },
];

// Innermost first, so the first limit a distance fits inside is its band.
const BANDS_INNERMOST_FIRST = [...PROXIMITY_BANDS].reverse();

// 128 segments keeps the outer band's edge smooth at the zoom where a 5 km ring
// fills the viewport; the default 64 is visibly polygonal there.
const CIRCLE_STEPS = 128;

const EMPTY_COLLECTION = { type: "FeatureCollection", features: [] };

const isPlottable = center =>
  Number.isFinite(center?.lat) && Number.isFinite(center?.lng);

// Ascending and positive is what makes the bands nest. The backend enforces it
// on save, but a hand-edited or partially written setting would otherwise
// produce an annulus with an inverted hole.
const isUsable = limits =>
  [limits?.min, limits?.mid, limits?.max].every(
    value => Number.isFinite(value) && value > 0
  ) &&
  limits.min < limits.mid &&
  limits.mid < limits.max;

const ringFor = (center, radiusMeters) =>
  circle([center.lng, center.lat], radiusMeters, {
    steps: CIRCLE_STEPS,
    units: "meters",
  }).geometry.coordinates[0];

/**
 * The proximity bands around one or more centres, as GeoJSON annuli.
 *
 * Each band is a polygon whose hole is the next band in — bands never overlap,
 * so a fill reads as one flat colour and a marker sits unambiguously in exactly
 * one band. The green band is a plain disc, having nothing inside it.
 *
 * @param {Object} args
 * @param {Array<{lat: number, lng: number}>} args.centers Ring anchors, usually
 *   the map's address pins.
 * @param {{min: number, mid: number, max: number}} args.limits Green, Yellow and
 *   Red distances in metres, as configured under Global Settings > Piracy.
 * @returns {Object} A FeatureCollection of annulus polygons, each carrying
 *   `properties.band` and `properties.radius`. Empty when there is no plottable
 *   centre or the limits are unusable, which is how a caller draws nothing.
 */
export function buildProximityRings({ centers = [], limits } = {}) {
  const anchors = centers.filter(isPlottable);

  if (!anchors.length || !isUsable(limits)) return EMPTY_COLLECTION;

  const features = anchors.flatMap((center, centerIndex) =>
    PROXIMITY_BANDS.map(({ band, limitKey }, bandIndex) => {
      const radius = limits[limitKey];
      const inner = PROXIMITY_BANDS[bandIndex + 1];

      return {
        type: "Feature",
        id: `proximity-${centerIndex}-${band}`,
        geometry: {
          type: "Polygon",
          coordinates: inner
            ? [ringFor(center, radius), ringFor(center, limits[inner.limitKey])]
            : [ringFor(center, radius)],
        },
        properties: { band, radius },
      };
    })
  );

  return { type: "FeatureCollection", features };
}

/**
 * The band a distance from an address pin falls in.
 *
 * Reads the same limits as the drawn rings, so a marker cannot be coloured for
 * one band while sitting inside another band's ring.
 *
 * @param {number} distanceMeters Metres from the nearest address pin.
 * @param {{min: number, mid: number, max: number}} limits Green, Yellow and Red
 *   distances in metres, as configured under Global Settings > Piracy.
 * @returns {"green"|"yellow"|"red"|"grey"} `grey` wherever no ring applies: an
 *   unknown distance, unusable limits, or a point beyond the outermost band.
 */
export function proximityBandFor(distanceMeters, limits) {
  if (!Number.isFinite(distanceMeters) || !isUsable(limits)) return "grey";

  const match = BANDS_INNERMOST_FIRST.find(
    ({ limitKey }) => distanceMeters <= limits[limitKey]
  );

  return match ? match.band : "grey";
}
